// Web turns are not WhatsApp messages (MYC-090; plan §4.17 and G-47).
//
// A my.echoflicks.com creation talks to Felix through the web timeline, but the
// bot still publishes the reused lifecycle events (story.received,
// user.input.sent, director.prompt_user, …) and the dashboard indexer projects
// those onto session MESSAGE rows. The WhatsApp history views must not render
// them as WhatsApp bubbles: the Echo page shows them in the Web transcript.
//
// A row is a web turn when:
//   - the indexer stamped it `channel: "web"` (MYC-015), or its raw payload
//     says so (`channel` / `channel_route.channel`), or it names a web
//     conversation id (`wbc_…`, contract C1);
//   - or it belongs to a web Echo and carries no WhatsApp provider message id.
//     Nothing on the web lane has one (contract C13: projections tolerate the
//     missing WhatsApp id), while a real WhatsApp send always does — so a
//     WhatsApp message on a web Echo (e.g. a ready notification) still shows.

import { payloadIsWebChannel } from "../../echoflicks/webEcho";

const WEB_CONVERSATION_ID_PREFIX = "wbc_";

type MaybeWebRow = {
  channel?: unknown;
  conversation_id?: unknown;
  provider_message_id?: unknown;
  raw_payload?: unknown;
};

const isWebConversationId = (value: unknown) =>
  typeof value === "string" && value.startsWith(WEB_CONVERSATION_ID_PREFIX);

export function isWebChannelMessage(row: MaybeWebRow): boolean {
  if (typeof row.channel === "string" && row.channel.toLowerCase() === "web") {
    return true;
  }
  if (isWebConversationId(row.conversation_id)) return true;
  const raw = row.raw_payload;
  if (payloadIsWebChannel(raw)) return true;
  return (
    typeof raw === "object" &&
    raw !== null &&
    isWebConversationId((raw as Record<string, unknown>).conversation_id)
  );
}

const hasProviderMessageId = (row: MaybeWebRow) =>
  typeof row.provider_message_id === "string" &&
  row.provider_message_id.trim() !== "";

/**
 * The rows a WhatsApp history view may render. `webEcho` is true when every
 * row belongs to one web Echo (the Echo page's Conversation card).
 */
export function whatsAppHistoryRows<T extends MaybeWebRow>(
  rows: T[],
  { webEcho = false }: { webEcho?: boolean } = {},
): T[] {
  return rows.filter(
    (row) =>
      !isWebChannelMessage(row) && (!webEcho || hasProviderMessageId(row)),
  );
}
